import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Warning,
  ArrowsDownUp,
  Key,
} from "@phosphor-icons/react";
import "@/App.css";
import { ApiReferenceDrawer } from "@/ApiReferenceDrawer";
import { MaintenanceBanner } from "@/components/MaintenanceBanner";
import { useExternalStatus } from "@/hooks";

import { API, BACKEND_URL, PROJECT_ID, http, getWithRetry, MODES, ACCOUNT_EXAMPLES, ADDRESS_EXAMPLES, ASSET_EXAMPLES, TX_EXAMPLES, POOL_EXAMPLES, EPOCH_EXAMPLES, MODE_TO_FILTER, FILTER_TO_MODE, CARDANO_NETWORK, METADATA_QUICK_PICKS } from "@/lib/constants";
import { cn } from "@/lib/format";
import { tabsFor, identifierMatchesMode } from "@/lib/tabs";
import { detectMode } from "@/lib/search";
import { detectGovTarget } from "@/lib/governance";
import { TxNavContext, AddrNavContext, BlockNavContext, AssetNavContext } from "@/lib/navContext";
import { Badge, CopyBtn, Mono, ShareLinkButton } from "@/components/primitives";
import { Header } from "@/components/Header";
import { SmartSearchBar } from "@/components/SmartSearchBar";
import { DataTab } from "@/components/DataTab";
import { SummaryPanel } from "@/components/panels/AccountSummaryPanel";
import { AddressSummaryPanel } from "@/components/panels/AddressSummaryPanel";
import { AssetProfile } from "@/components/assets/AssetProfile";
import { BlockSummaryPanel } from "@/components/panels/BlockSummaryPanel";
import { TransactionSummaryPanel } from "@/components/panels/TransactionSummaryPanel";
import { PoolSummaryPanel } from "@/components/panels/PoolSummaryPanel";
import { EpochSummaryPanel } from "@/components/panels/EpochSummaryPanel";
import { MetadataSummaryPanel } from "@/components/panels/MetadataSummaryPanel";
import { HomePage } from "@/pages/HomePage";
import { NativeAssetsPage } from "@/pages/NativeAssetsPage";
import { AssetExplorerPage } from "@/pages/AssetExplorerPage";
import { GovernanceRouter } from "@/pages/governance/GovernanceRouter";
import { useVoteAlertRunner } from "@/lib/voteWatch";

// Parse the current browser URL into nav state so deep-links and the browser
// Back/Forward buttons work (the app is otherwise pure React state).
// URL scheme: "/" = home, "/governance/*" = governance explorer,
// "/{mode}/{identifier}?tab={tab}" = chain explorer view.
const parseLocation = () => {
  try {
    const path = window.location.pathname.replace(/^\/+|\/+$/g, "");
    if (!path) return { view: "home", modeKey: "accounts", id: null, tab: null, govPath: "/governance" };
    if (path === "governance" || path.startsWith("governance/")) {
      return { view: "governance", modeKey: "accounts", id: null, tab: null, govPath: `/${path}` };
    }
    if (path === "native-assets") {
      return { view: "assets-dashboard", modeKey: "accounts", id: null, tab: null, govPath: "/governance" };
    }
    if (path === "asset-explorer") {
      return { view: "asset-explorer", modeKey: "accounts", id: null, tab: null, govPath: "/governance" };
    }
    const [mk, ...rest] = path.split("/");
    if (!MODES[mk]) return { view: "home", modeKey: "accounts", id: null, tab: null, govPath: "/governance" };
    const id = rest.length ? decodeURIComponent(rest.join("/")) : null;
    const tabParam = new URLSearchParams(window.location.search).get("tab");
    return { view: "explorer", modeKey: mk, id, tab: tabParam || MODES[mk].defaultTab, govPath: "/governance" };
  } catch {
    return { view: "home", modeKey: "accounts", id: null, tab: null, govPath: "/governance" };
  }
};

const INITIAL_NAV = parseLocation();

// ---------- main app ----------
function App() {
  const [modeKey, setModeKey] = useState(INITIAL_NAV.modeKey);
  const mode = MODES[modeKey];
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [view, setView] = useState(INITIAL_NAV.view); // 'home' | 'explorer' | 'governance'
  const [govPath, setGovPath] = useState(INITIAL_NAV.govPath || "/governance");

  const [query, setQuery] = useState(INITIAL_NAV.id ?? MODES[INITIAL_NAV.modeKey].examples[0] ?? "");
  const [activeId, setActiveId] = useState(null);
  const [summary, setSummary] = useState(null);
  const [summaryError, setSummaryError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [slowLoad, setSlowLoad] = useState(false);
  const [tab, setTab] = useState(INITIAL_NAV.tab ?? mode.defaultTab);
  const [amountMode, setAmountMode] = useState("ada"); // 'ada' | 'lovelace'
  const { maintenance, apiStatus } = useExternalStatus();

  // Fires browser alerts for watched governance actions before voting closes.
  useVoteAlertRunner();

  // Bumped on every runQuery call AND every mode switch, so a slow/stale
  // response (e.g. the previous mode's request still in flight when the user
  // flips to a new mode) can never overwrite state committed by a later one.
  const requestGenRef = useRef(0);

  // Tracks the live search text so an in-flight query's result can't clobber
  // the input after the user has typed something new (e.g. pasting a governance
  // id on /blocks/latest while the initial 'latest' lookup is still resolving).
  const queryRef = useRef(query);
  queryRef.current = query;

  // The external Cardano data API can occasionally take 10-40s+ per query —
  // surface a hint after 5s so a long wait doesn't read as a hung page.
  useEffect(() => {
    if (!loading) {
      setSlowLoad(false);
      return;
    }
    const t = setTimeout(() => setSlowLoad(true), 5000);
    return () => clearTimeout(t);
  }, [loading]);

  const runQuery = useCallback(async (override, overrideModeKey) => {
    const mk = overrideModeKey || modeKey;
    const m = MODES[mk];
    const target = (override ?? query ?? "").trim();
    if (!target) return;
    const myGen = ++requestGenRef.current;
    setLoading(true);
    setSummary(null);
    setSummaryError(null);
    try {
      if (mk === "metadata") {
        // Metadata labels have no single "object" endpoint (mirrors Blockfrost) —
        // probe the same paginated list endpoint the Entries tab uses, and build a
        // lightweight synthetic summary from the first row.
        const res = await getWithRetry(`${API}${m.basePath}/${target}`, { params: { count: 1, page: 1 } });
        if (requestGenRef.current !== myGen) return; // a newer request/mode switch superseded this one
        const rows = Array.isArray(res.data) ? res.data : [];
        setSummary({ label: target, has_entries: rows.length > 0, sample: rows[0] || null });
        setActiveId(target);
        return;
      }
      if (mk === "addresses") {
        // Validate via /info first: it returns 200 for any valid address
        // (used or not) and 400 for invalid/wrong-network addresses. We never
        // 404 a valid-but-unused address.
        let info;
        try {
          const infoRes = await getWithRetry(`${API}/addresses/${target}/info`, { timeout: 15000 });
          info = infoRes.data;
        } catch (e) {
          if (requestGenRef.current !== myGen) return;
          const st = e.response?.status;
          if (st === 400) {
            const body = e.response?.data || {};
            setSummaryError({
              status_code: 400,
              error: "Invalid address",
              message: body.message || "This is not a valid Cardano address.",
            });
          } else {
            setSummaryError(e.response?.data?.detail || e.response?.data || { message: e.message });
          }
          setActiveId(null);
          return;
        }
        if (requestGenRef.current !== myGen) return;
        // Load live balances/tokens only when the address has (or may have)
        // on-chain activity. For used:false we never hit the detail endpoint
        // (it may 404); for used:null we try but tolerate a 404.
        let detail = null;
        if (info.used === true || info.used === null) {
          try {
            const dRes = await getWithRetry(`${API}/addresses/${target}`, { timeout: 15000 });
            detail = dRes.data;
          } catch (e) {
            // used:null + 404 → simply unknown activity; swallow.
          }
        }
        const summaryObj = {
          ...(detail || {}),
          address: info.address,
          type: (detail && detail.type) || info.address_type,
          script: info.script,
          stake_address: info.stake_address,
          _info: info,
          _used: info.used === true ? true : info.used === false ? false : null,
        };
        if (info.used === false) {
          summaryObj.amount = [{ unit: "lovelace", quantity: "0" }];
        }
        setSummary(summaryObj);
        setActiveId(target);
        return;
      }
      const res = await getWithRetry(`${API}${m.basePath}/${target}`);
      if (requestGenRef.current !== myGen) return; // a newer request/mode switch superseded this one
      setSummary(res.data);
      // For blocks, normalize the active identifier to the block number so that
      // tab sub-endpoints (e.g. /blocks/{n}/txs) work regardless of whether the
      // user searched by 'latest', a number, or a hash.
      if (mk === "blocks" && res.data?.height != null) {
        setActiveId(String(res.data.height));
        if (queryRef.current === target) setQuery(String(res.data.height));
      } else if (mk === "epochs" && res.data?.epoch != null) {
        setActiveId(String(res.data.epoch));
        if (queryRef.current === target) setQuery(String(res.data.epoch));
      } else {
        setActiveId(target);
      }
    } catch (e) {
      if (requestGenRef.current !== myGen) return; // a newer request/mode switch superseded this one
      setSummaryError(e.response?.data?.detail || { message: e.message });
      setActiveId(null);
    } finally {
      if (requestGenRef.current === myGen) setLoading(false);
    }
  }, [query, modeKey]);

  const switchMode = useCallback((newKey) => {
    if (newKey === modeKey) return;
    const newMode = MODES[newKey];
    setModeKey(newKey);
    setTab(newMode.defaultTab);
    setSummary(null);
    setActiveId(null);
    setSummaryError(null);
    if (newKey === "metadata") {
      // Invalidate any request still in flight from the previous mode so its
      // late response can't repopulate summary/query and hide the empty state.
      requestGenRef.current += 1;
      setLoading(false);
      // Leave the search empty rather than auto-loading a default label, so the
      // quick-pick chips (674/721/20) in the empty state are actually reachable.
      setQuery("");
      return;
    }
    setQuery(newMode.examples[0]);
    // auto-query first example of new mode
    runQuery(newMode.examples[0], newKey);
  }, [modeKey, runQuery]);

  useEffect(() => {
    if (view === "explorer") {
      runQuery();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the latest runQuery available to the one-time popstate listener.
  const runQueryRef = useRef(runQuery);
  useEffect(() => {
    runQueryRef.current = runQuery;
  });

  // Reflect nav state into the URL/browser history so Back/Forward work.
  // Only commit "settled" views (home, or explorer with a loaded identifier) so
  // transient loading/error states don't create junk history entries.
  const firstSyncRef = useRef(INITIAL_NAV.view === "explorer");
  useEffect(() => {
    // Governance owns its own URL (pushed by govNavigate); never let the
    // explorer URL-sync overwrite a /governance/* location.
    if (view === "governance" || view === "assets-dashboard" || view === "asset-explorer") return;
    const url =
      view === "home"
        ? "/"
        : activeId
          ? `/${modeKey}/${encodeURIComponent(activeId)}?tab=${tab}`
          : null;
    if (!url) return;
    const current = window.location.pathname + window.location.search;
    if (url === current) return;
    if (firstSyncRef.current) {
      firstSyncRef.current = false;
      window.history.replaceState(null, "", url);
    } else {
      window.history.pushState(null, "", url);
    }
  }, [view, modeKey, activeId, tab]);

  // Restore nav state when the user presses Back/Forward.
  useEffect(() => {
    const onPop = () => {
      const s = parseLocation();
      if (s.view === "governance") {
        setGovPath(s.govPath);
        setView("governance");
        window.scrollTo({ top: 0 });
        return;
      }
      if (s.view === "home") {
        setView("home");
        setSummary(null);
        setSummaryError(null);
        setActiveId(null);
      } else if (s.view === "assets-dashboard") {
        setView("assets-dashboard");
      } else if (s.view === "asset-explorer") {
        setView("asset-explorer");
      } else {
        setModeKey(s.modeKey);
        setTab(s.tab);
        setView("explorer");
        if (s.id) {
          setQuery(s.id);
          setActiveId(s.id); // keep state in sync with the URL so no extra push
          runQueryRef.current(s.id, s.modeKey);
        } else {
          setActiveId(null);
          setSummary(null);
          setSummaryError(null);
        }
      }
      window.scrollTo({ top: 0 });
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const tabs = tabsFor(modeKey);
  const subjectBadge =
    mode.key === "accounts"
      ? "stake_address"
      : mode.key === "assets"
        ? "asset"
        : mode.key === "blocks"
          ? "block"
          : mode.key === "transactions"
            ? "tx_hash"
            : mode.key === "pools"
              ? "pool_id"
              : mode.key === "epochs"
                ? "epoch"
                : mode.key === "metadata"
                  ? "label"
                  : "address";
  const subjectValue =
    (mode.key === "addresses" ? summary?.address : null) ||
    summary?.stake_address ||
    summary?.address ||
    summary?.asset ||
    (mode.key === "blocks" ? (summary?.height != null ? `#${summary.height} · ${summary?.hash}` : null) : null) ||
    (mode.key === "transactions" ? summary?.hash : null) ||
    (mode.key === "pools" ? summary?.pool_id : null) ||
    (mode.key === "epochs" ? (summary?.epoch != null ? `Epoch ${summary.epoch}` : null) : null) ||
    (mode.key === "metadata" ? (summary?.label != null ? `Label ${summary.label}` : null) : null);

  // Keep the document title + social/meta tags in sync with the current view,
  // so the browser tab and shared links reflect the exact subject being viewed.
  useEffect(() => {
    const net = CARDANO_NETWORK === "mainnet" ? "mainnet" : "preprod";
    const site = "Ada Explorer";
    let title;
    let desc;
    if (view === "home" || !subjectValue) {
      title = `${site} · Cardano ${net} blockchain explorer`;
      desc = `Search any address, stake key, transaction, block, asset or pool on the Cardano ${net}.`;
    } else {
      const label = String(subjectBadge).replace(/_/g, " ");
      const sv = String(subjectValue);
      const shortVal = sv.length > 26 ? `${sv.slice(0, 12)}…${sv.slice(-6)}` : sv;
      title = `${label}: ${shortVal} · ${site}`;
      desc = `${label} ${sv} on the Cardano ${net} — view full details on ${site}.`;
    }
    document.title = title;
    const setMeta = (selector, content) => {
      const el = document.head.querySelector(selector);
      if (el) el.setAttribute("content", content);
    };
    setMeta('meta[name="description"]', desc);
    setMeta('meta[property="og:title"]', title);
    setMeta('meta[name="twitter:title"]', title);
    setMeta('meta[property="og:description"]', desc);
    setMeta('meta[name="twitter:description"]', desc);
    setMeta('meta[property="og:url"]', window.location.href);
  }, [view, modeKey, subjectValue, subjectBadge]);

  // Click any tx hash anywhere → switch to Transactions mode and load it.
  const pickTx = useCallback((hash) => {
    if (!hash) return;
    setModeKey("transactions");
    setTab(MODES.transactions.defaultTab);
    setQuery(hash);
    setActiveId(null);
    runQuery(hash, "transactions");
    setView("explorer");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [runQuery]);

  // Home page → switch into explorer mode and load the chosen subject.
  const goExplorer = useCallback((newModeKey, value) => {
    const m = MODES[newModeKey];
    if (!m) return;
    setModeKey(newModeKey);
    setTab(m.defaultTab);
    setQuery(value);
    setActiveId(null);
    setSummary(null);
    setSummaryError(null);
    setView("explorer");
    runQuery(value, newModeKey);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [runQuery]);

  // Navigate within the Governance explorer. App owns the URL/history; we push
  // the route here and flip the view so popstate stays consistent.
  const govNavigate = useCallback((to) => {
    if (!to) return;
    const path = to.startsWith("/") ? to : `/${to}`;
    if (window.location.pathname + window.location.search !== path) {
      window.history.pushState(null, "", path);
    }
    setGovPath(path);
    setView("governance");
    window.scrollTo({ top: 0 });
  }, []);

  // Open the Native Assets analytics dashboard (owns its own /native-assets URL).
  const goAssetsDashboard = useCallback(() => {
    if (window.location.pathname !== "/native-assets") {
      window.history.pushState(null, "", "/native-assets");
    }
    setView("assets-dashboard");
    window.scrollTo({ top: 0 });
  }, []);

  // Open the full Asset Explorer (its own /asset-explorer URL).
  const goAssetExplorer = useCallback(() => {
    if (window.location.pathname !== "/asset-explorer") {
      window.history.pushState(null, "", "/asset-explorer");
    }
    setView("asset-explorer");
    window.scrollTo({ top: 0 });
  }, []);

  // Explorer search bar submit — auto-detect the pasted identifier's type so users
  // don't have to manually flip the filter dropdown first. If the text is already
  // valid for the current mode (e.g. a numeric epoch/block/metadata label, which are
  // ambiguous across modes), stay put. Otherwise, if it matches a different mode's
  // shape, switch straight to that mode.
  const onSmartSubmit = useCallback(() => {
    const q = (query || "").trim();
    if (!q) return;
    // Governance actions / DReps jump straight into the governance explorer.
    const gt = detectGovTarget(q);
    if (gt) {
      govNavigate(gt.path);
      return;
    }
    if (identifierMatchesMode(modeKey, q)) {
      runQuery();
      return;
    }
    const det = detectMode(q);
    if (det?.mode && det.mode !== modeKey) {
      goExplorer(det.mode, det.value ?? q);
    } else {
      runQuery();
    }
  }, [query, modeKey, goExplorer, runQuery, govNavigate]);

  // Click any address anywhere → switch to Addresses mode and load it.
  const pickAddress = useCallback((addr) => {
    if (!addr) return;
    goExplorer("addresses", addr);
  }, [goExplorer]);

  const pickBlock = useCallback((h) => {
    if (h == null || h === "") return;
    goExplorer("blocks", String(h));
  }, [goExplorer]);

  const pickAsset = useCallback((unit) => {
    if (!unit) return;
    goExplorer("assets", unit);
  }, [goExplorer]);

  if (view === "governance") {
    return (
      <TxNavContext.Provider value={pickTx}><AddrNavContext.Provider value={pickAddress}><BlockNavContext.Provider value={pickBlock}><AssetNavContext.Provider value={pickAsset}>
        <div className="min-h-screen bg-background text-foreground grid-bg">
          <MaintenanceBanner maintenance={maintenance} apiStatus={apiStatus} />
          <Header
            onOpenReference={() => setDrawerOpen(true)}
            onGoHome={() => setView("home")}
            currentView="governance"
            onGovNavigate={govNavigate}
            onOpenAssets={goAssetsDashboard}
            govPath={govPath}
          />
          <ApiReferenceDrawer
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            backendUrl={BACKEND_URL}
            projectId={PROJECT_ID}
            httpClient={http}
            defaults={{
              stake_address: ACCOUNT_EXAMPLES[0],
              address: ADDRESS_EXAMPLES[0],
              asset: ASSET_EXAMPLES[0],
              policy_id: ASSET_EXAMPLES[0].slice(0, 56),
              hash_or_number: "latest",
              slot_number: "",
              epoch_number: EPOCH_EXAMPLES[1] || "latest",
              pool_id: POOL_EXAMPLES[0],
              hash: TX_EXAMPLES[0],
            }}
          />
          <GovernanceRouter path={govPath} navigate={govNavigate} />
          <footer className="border-t border-primary/15 mt-16">
            <div className="max-w-[1400px] mx-auto px-6 py-6 flex items-center justify-between gap-4 flex-wrap text-[10px] uppercase tracking-[0.2em] text-muted-foreground mono">
              <span>ada explorer · governance · {CARDANO_NETWORK === "mainnet" ? "mainnet" : "preprod testnet"}</span>
              <a
                href={BACKEND_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 hover:text-primary transition-colors"
              >
                powered by ada.api.gridscan.io
              </a>
            </div>
          </footer>
        </div>
      </AssetNavContext.Provider></BlockNavContext.Provider></AddrNavContext.Provider></TxNavContext.Provider>
    );
  }

  if (view === "assets-dashboard") {
    return (
      <TxNavContext.Provider value={pickTx}><AddrNavContext.Provider value={pickAddress}><BlockNavContext.Provider value={pickBlock}><AssetNavContext.Provider value={pickAsset}>
        <div className="min-h-screen bg-background text-foreground grid-bg">
          <MaintenanceBanner maintenance={maintenance} apiStatus={apiStatus} />
          <Header
            onOpenReference={() => setDrawerOpen(true)}
            onGoHome={() => setView("home")}
            currentView="assets-dashboard"
            onGovNavigate={govNavigate}
            onOpenAssets={goAssetsDashboard}
            govPath={govPath}
          />
          <NativeAssetsPage onPickAsset={(unit) => goExplorer("assets", unit)} onOpenExplorer={goAssetExplorer} />
          <footer className="border-t border-primary/15 mt-16">
            <div className="max-w-[1400px] mx-auto px-6 py-6 flex items-center justify-between gap-4 flex-wrap text-[10px] uppercase tracking-[0.2em] text-muted-foreground mono">
              <span>ada explorer · native assets · {CARDANO_NETWORK === "mainnet" ? "mainnet" : "preprod testnet"}</span>
              <a href={BACKEND_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 hover:text-primary transition-colors">
                powered by ada.api.gridscan.io
              </a>
            </div>
          </footer>
        </div>
      </AssetNavContext.Provider></BlockNavContext.Provider></AddrNavContext.Provider></TxNavContext.Provider>
    );
  }

  if (view === "asset-explorer") {
    return (
      <TxNavContext.Provider value={pickTx}><AddrNavContext.Provider value={pickAddress}><BlockNavContext.Provider value={pickBlock}><AssetNavContext.Provider value={pickAsset}>
        <div className="min-h-screen bg-background text-foreground grid-bg">
          <MaintenanceBanner maintenance={maintenance} apiStatus={apiStatus} />
          <Header
            onOpenReference={() => setDrawerOpen(true)}
            onGoHome={() => setView("home")}
            currentView="assets-dashboard"
            onGovNavigate={govNavigate}
            onOpenAssets={goAssetsDashboard}
            govPath={govPath}
          />
          <AssetExplorerPage onPickAsset={(unit) => goExplorer("assets", unit)} />
          <footer className="border-t border-primary/15 mt-16">
            <div className="max-w-[1400px] mx-auto px-6 py-6 flex items-center justify-between gap-4 flex-wrap text-[10px] uppercase tracking-[0.2em] text-muted-foreground mono">
              <span>ada explorer · asset explorer · {CARDANO_NETWORK === "mainnet" ? "mainnet" : "preprod testnet"}</span>
              <a href={BACKEND_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 hover:text-primary transition-colors">
                powered by ada.api.gridscan.io
              </a>
            </div>
          </footer>
        </div>
      </AssetNavContext.Provider></BlockNavContext.Provider></AddrNavContext.Provider></TxNavContext.Provider>
    );
  }

  if (view === "home") {
    return (
      <TxNavContext.Provider value={pickTx}><AddrNavContext.Provider value={pickAddress}><BlockNavContext.Provider value={pickBlock}><AssetNavContext.Provider value={pickAsset}>
        <div className="min-h-screen bg-background text-foreground">
          <MaintenanceBanner maintenance={maintenance} apiStatus={apiStatus} />
          <Header
            onOpenReference={() => setDrawerOpen(true)}
            onGoHome={() => setView("home")}
            currentView="home"
            onGovNavigate={govNavigate}
            onOpenAssets={goAssetsDashboard}
            govPath={govPath}
          />
          <ApiReferenceDrawer
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            backendUrl={BACKEND_URL}
            projectId={PROJECT_ID}
            httpClient={http}
            defaults={{
              stake_address: ACCOUNT_EXAMPLES[0],
              address: ADDRESS_EXAMPLES[0],
              asset: ASSET_EXAMPLES[0],
              policy_id: ASSET_EXAMPLES[0].slice(0, 56),
              hash_or_number: "latest",
              slot_number: "",
              epoch_number: EPOCH_EXAMPLES[1] || "latest",
              pool_id: POOL_EXAMPLES[0],
              hash: TX_EXAMPLES[0],
            }}
          />
          <HomePage
            onSearch={goExplorer}
            onGovSearch={govNavigate}
            amountMode={amountMode}
            onPickBlock={(height) => goExplorer("blocks", String(height))}
            onPickTx={pickTx}
            onPickPool={(pool_id) => goExplorer("pools", pool_id)}
          />
        </div>
      </AssetNavContext.Provider></BlockNavContext.Provider></AddrNavContext.Provider></TxNavContext.Provider>
    );
  }

  return (
    <TxNavContext.Provider value={pickTx}><AddrNavContext.Provider value={pickAddress}><BlockNavContext.Provider value={pickBlock}><AssetNavContext.Provider value={pickAsset}>
    <div className="min-h-screen bg-background text-foreground grid-bg">
      <MaintenanceBanner maintenance={maintenance} apiStatus={apiStatus} />
      <Header
        onOpenReference={() => setDrawerOpen(true)}
        onGoHome={() => setView("home")}
        currentView="explorer"
        onGovNavigate={govNavigate}
        onOpenAssets={goAssetsDashboard}
        govPath={govPath}
      />

      <ApiReferenceDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        backendUrl={BACKEND_URL}
        projectId={PROJECT_ID}
        httpClient={http}
        defaults={{
          stake_address: modeKey === "accounts" ? activeId || ACCOUNT_EXAMPLES[0] : ACCOUNT_EXAMPLES[0],
          address: modeKey === "addresses" ? activeId || ADDRESS_EXAMPLES[0] : ADDRESS_EXAMPLES[0],
          asset: modeKey === "assets" ? activeId || ASSET_EXAMPLES[0] : ASSET_EXAMPLES[0],
          policy_id: (modeKey === "assets" && activeId ? activeId.slice(0, 56) : ASSET_EXAMPLES[0].slice(0, 56)),
          hash_or_number: modeKey === "blocks" && activeId ? activeId : "latest",
          slot_number: summary?.slot ? String(summary.slot) : "",
          epoch_number: summary?.epoch != null ? String(summary.epoch) : (EPOCH_EXAMPLES[1] || "latest"),
          pool_id: POOL_EXAMPLES[0],
          number: EPOCH_EXAMPLES[1] || "latest",
          hash: TX_EXAMPLES[0],
        }}
      />

      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 sm:py-10 space-y-8 sm:space-y-10">
        {/* hero / search */}
        <section className="relative">
          <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
          <div className="relative space-y-8">
            <div className="flex items-end justify-between gap-6 flex-wrap">
              <div>
                <h1 className="font-heading text-2xl md:text-3xl font-semibold tracking-tight">
                  {mode.label}
                </h1>
                <p className="text-[12px] text-muted-foreground mono mt-2">
                  on <span className={CARDANO_NETWORK === "mainnet" ? "text-emerald-400" : "text-yellow-400"}>Cardano {CARDANO_NETWORK === "mainnet" ? "mainnet" : "preprod testnet"}</span> · powered by ada.api.gridscan.io
                </p>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <button
                  data-testid="toggle-amount-mode"
                  onClick={() => setAmountMode(amountMode === "ada" ? "lovelace" : "ada")}
                  className="inline-flex items-center gap-2 border border-primary/20 bg-card/40 rounded-lg px-3 py-2 text-[10px] uppercase tracking-[0.2em] hover:bg-muted/30 transition-colors"
                >
                  <ArrowsDownUp size={12} />
                  {amountMode === "ada" ? "ADA (₳)" : "Lovelace"}
                </button>
              </div>
            </div>
            <SmartSearchBar
              value={query}
              onChange={setQuery}
              filter={MODE_TO_FILTER[modeKey] || "all"}
              onFilterChange={(filterKey) => {
                const nextMode = FILTER_TO_MODE[filterKey];
                if (filterKey === "all" || !nextMode || nextMode === modeKey) return;
                switchMode(nextMode);
              }}
              onSubmit={onSmartSubmit}
              loading={loading}
              placeholder={mode.placeholder}
            />
          </div>
        </section>

        {/* results / summary */}
        {summaryError ? (
          <div
            data-testid="summary-error"
            className="border border-red-500/40 bg-red-500/5 text-red-400 p-5 flex items-start gap-3"
          >
            <Warning size={18} className="mt-0.5" />
            <div>
              <div className="text-sm font-semibold">
                {summaryError.status_code || "Error"} · {summaryError.error || "Request failed"}
              </div>
              <div className="mono text-[12px] text-red-300/80 mt-1">
                {summaryError.message}
              </div>
            </div>
          </div>
        ) : null}

        {summary ? (
          <section data-testid="summary-section" className="space-y-6">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge tone="info">{subjectBadge}</Badge>
              <Mono className="text-foreground break-all">{subjectValue}</Mono>
              <CopyBtn value={subjectValue} label={subjectBadge} />
              <div className="ml-auto">
                <ShareLinkButton />
              </div>
            </div>
            {(() => {
              const summaryPanel =
                modeKey === "accounts" && summary?.controlled_amount !== undefined ? (
                  <SummaryPanel
                    data={summary}
                    amountMode={amountMode}
                    onViewAddresses={() => setTab("addresses")}
                    onPickPool={(pool_id) => goExplorer("pools", pool_id)}
                  />
                ) : modeKey === "assets" && summary?.fingerprint ? (
                  <AssetProfile data={summary} onOpenPolicy={() => setTab("policy")} />
                ) : modeKey === "blocks" && summary?.height !== undefined ? (
                  <BlockSummaryPanel
                    data={summary}
                    amountMode={amountMode}
                    onNavigate={(target) => {
                      setQuery(target);
                      runQuery(target);
                    }}
                  />
                ) : modeKey === "transactions" && summary?.hash ? (
                  <TransactionSummaryPanel
                    data={summary}
                    amountMode={amountMode}
                    onPickBlock={(height) => {
                      const target = String(height);
                      setModeKey("blocks");
                      setTab(MODES.blocks.defaultTab);
                      setQuery(target);
                      runQuery(target, "blocks");
                    }}
                  />
                ) : modeKey === "addresses" && summary?.address ? (
                  <AddressSummaryPanel data={summary} amountMode={amountMode} />
                ) : modeKey === "pools" && summary?.pool_id ? (
                  <PoolSummaryPanel data={summary} amountMode={amountMode} />
                ) : modeKey === "epochs" && summary?.epoch !== undefined ? (
                  <EpochSummaryPanel
                    data={summary}
                    amountMode={amountMode}
                    onNavigateBlock={(h) => goExplorer("blocks", String(h))}
                  />
                ) : modeKey === "metadata" && summary?.label !== undefined ? (
                  <MetadataSummaryPanel data={summary} onPickTx={pickTx} />
                ) : null;

              const tabsBlock = (
                <>
                  <div className="border-b border-primary/15 flex items-end gap-1 overflow-x-auto">
                    {tabs.map((t) => (
                      <button
                        key={t.key}
                        data-testid={`tab-${t.key}`}
                        onClick={() => setTab(t.key)}
                        className={cn(
                          "px-5 py-3 text-[11px] uppercase tracking-[0.18em] border-b-2 -mb-px transition-colors whitespace-nowrap",
                          tab === t.key
                            ? "border-primary text-primary"
                            : "border-transparent text-muted-foreground hover:text-foreground",
                        )}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                  <DataTab
                    key={`${modeKey}-${tab}-${activeId || ""}`}
                    mode={mode}
                    tab={tab}
                    identifier={activeId}
                    amountMode={amountMode}
                    onPickTx={pickTx}
                    onPickBlock={(height) => goExplorer("blocks", String(height))}
                    stakeAddress={
                      modeKey === "addresses"
                        ? summary?.stake_address
                        : modeKey === "accounts"
                        ? activeId
                        : undefined
                    }
                  />
                </>
              );

              if (modeKey === "pools" || modeKey === "epochs") {
                return (
                  <div className="grid lg:grid-cols-[420px_1fr] gap-6 items-start">
                    <div className="lg:sticky lg:top-4">{summaryPanel}</div>
                    <div className="space-y-6 min-w-0">{tabsBlock}</div>
                  </div>
                );
              }
              return (
                <>
                  {summaryPanel}
                  {tabsBlock}
                </>
              );
            })()}
          </section>
        ) : null}

        {loading ? (
          <div className="space-y-3">
            {slowLoad ? (
              <div
                data-testid="slow-load-hint"
                className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground mono flex items-center gap-2"
              >
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
                still loading — the data source can be slow, hang tight
              </div>
            ) : null}
            <div data-testid="summary-loading" className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={`summary-skeleton-${i}`}
                  className="border border-primary/15 bg-card/60 p-5 flex flex-col gap-2.5 rounded-lg"
                >
                  <div className="h-2.5 w-20 bg-muted/30 animate-pulse rounded-sm" />
                  <div className="h-4 w-32 bg-muted/30 animate-pulse rounded-sm" />
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {!summary && !summaryError && !loading ? (
          modeKey === "metadata" ? (
            <div data-testid="metadata-empty-state" className="space-y-3">
              <div className="text-muted-foreground mono text-[13px]">
                Enter a {mode.subject} above to begin, or try a popular label:
              </div>
              <div className="flex flex-wrap gap-2">
                {METADATA_QUICK_PICKS.map((p) => (
                  <button
                    key={p.label}
                    data-testid={`metadata-quick-pick-${p.label}`}
                    onClick={() => {
                      setQuery(p.label);
                      runQuery(p.label);
                    }}
                    className="px-3 py-1.5 text-[11px] mono uppercase tracking-[0.18em] bg-card/40 border border-primary/15 text-muted-foreground hover:text-primary hover:border-primary/40 transition-colors"
                  >
                    {p.label}
                    <span className="text-muted-foreground/60 normal-case tracking-normal"> · {p.desc}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-muted-foreground mono text-[13px]">
              Enter a {mode.subject} above to begin.
            </div>
          )
        ) : null}
      </main>

      <footer className="border-t border-primary/15 mt-16">
        <div className="max-w-[1400px] mx-auto px-6 py-6 flex items-center justify-between gap-4 flex-wrap text-[10px] uppercase tracking-[0.2em] text-muted-foreground mono">
          <span>leios explorer · {mode.label.toLowerCase()} explorer · {CARDANO_NETWORK === "mainnet" ? "mainnet" : "preprod testnet"}</span>
          <span className="flex items-center gap-4 flex-wrap">
            <a
              href={BACKEND_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="footer-powered-by"
              className="inline-flex items-center gap-1.5 hover:text-primary transition-colors"
            >
              powered by ada.api.gridscan.io
            </a>
            <span className="inline-flex items-center gap-2">
              <Key size={11} /> project_id: {PROJECT_ID.slice(0, 12)}…
            </span>
          </span>
        </div>
      </footer>
    </div>
    </AssetNavContext.Provider></BlockNavContext.Provider></AddrNavContext.Provider></TxNavContext.Provider>
  );
}

export default App;
